import { fromPairs, toPairs, uniqBy } from 'lodash';

import { danger } from 'redux-store/reducers/common-reducer';
import { deletePosts } from 'redux-store/reducers/instagramPostsDeleteReducer';

import { createReducer } from 'utils/redux';
import { API, get } from 'utils/api';
import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';
import { getPostCodeFromUrl, getTikTokVideoCodeFromUrl } from 'utils/posts';
import { extendWithInstagramThumbnailUrl, extendWithTikTokThumbnailUrl } from 'utils/content/thumbnails';

import type {
  Action,
  PaginationMeta,
  ProposalImage,
  ThunkAction
} from 'types';
import type { ProposalTileChannelType } from 'modules/proposal/types/ProposalTile.type';
import { setLoadingProfileIds } from './proposal-page';

applyTranslations(require('./proposal-reducer-errors.i18n.json'));

const SET_MEDIA = 'creatorRecentMedia/SET_MEDIA';
const SET_LOADING_STATUS = 'creatorRecentMedia/SET_LOADING_STATUS';
const RESET = 'creatorRecentMedia/RESET';

export type State = {
  currentlyLoadedCreatorId: number | undefined | null,
  currentlyLoadedCreatorChannelId: number | undefined,
  currentlyLoadedPage: number,
  isOnLastPage: boolean,
  loadingStatus: boolean,
  posts: Array<ProposalImage>
};

const initialState: State = {
  currentlyLoadedCreatorId: undefined,
  currentlyLoadedCreatorChannelId: undefined,
  currentlyLoadedPage: 0,
  isOnLastPage: false,
  loadingStatus: false,
  posts: []
};

function setLoadingStatus(loadingStatus: boolean) {
  return {
    type: SET_LOADING_STATUS,
    payload: {
      loadingStatus
    }
  };
}

function resetMedia(): Action {
  return {
    type: RESET
  };
}

function setMedia(
  posts: Array<ProposalImage>,
  channelId: number,
  meta: PaginationMeta,
  profileId: number,
  page: number
) {
  return {
    type: SET_MEDIA,
    payload: {
      posts,
      channelId,
      meta,
      profileId,
      page
    }
  };
}

const ChannelType: Record<ProposalTileChannelType, number> = {
  instagram: 1,
  tiktok: 9
};

const ChannelTypeFromId: Record<number, ProposalTileChannelType> =
  fromPairs(
    toPairs(ChannelType)
      .map(pair => pair.reverse())
  );

function getImagePromises(channelId: number, images: Array<ProposalImage>): Array<Promise<ProposalImage>> {
  if (channelId === ChannelType.instagram) {
    return images.map((post) => extendWithInstagramThumbnailUrl(post));
  }

  if (channelId === ChannelType.tiktok) {
    return images.map((post) => extendWithTikTokThumbnailUrl(post));
  }

  return [];
}

type RecentImagesPageRespone = {
  data: Array<ProposalImage>,
  meta: PaginationMeta
};

async function fetchRecentImagesPage(profileId: number, channelId: number, page: number): Promise<RecentImagesPageRespone> {
  const url = API.END_POINTS.CREATOR_PROPOSAL.creatorRecentMedia(profileId, channelId, page);
  const response = await get(url, {}, { suppressGlobalNotification: true }) as { data: RecentImagesPageRespone };
  return response.data;
}

export function getRecentMediaExceptGivenUrls(
  profileId: number,
  channelId: number,
  numberOfImages: number,
  ignoredUrls: Array<string>,
  triggerError?: boolean
): ThunkAction {
  return async (dispatch, getState): Promise<Array<ProposalImage>> => {
    let results: Array<ProposalImage> = [];
    let page = 1;
    const currentState = getState().creatorRecentMedia;
    let loadingProfileIds = getState().proposalCreatePage.loadingProfileIds;
    const consents = getState().consentReducer.consents;

    const channel = ChannelTypeFromId[channelId];
    const hasConsent = consents[channel];

    if (
      currentState.currentlyLoadedCreatorId === profileId
      && currentState.currentlyLoadedCreatorChannelId === channelId
      && currentState.currentlyLoadedPage >= page
    ) {
      // if already loaded this profile and page is also loaded
      page = currentState.currentlyLoadedPage + 1;
      results = currentState.posts.filter(post => post.url && post.assetUrl && !ignoredUrls.includes(post.url));
    }

    dispatch(setLoadingProfileIds({ ...loadingProfileIds, [profileId]: true }));

    try {
      // fetch pages one by one until have enough data
      while (results.length < numberOfImages) {
        const { data, meta } = await fetchRecentImagesPage(profileId, channelId, page); // eslint-disable-line no-await-in-loop
        page += 1;

        if (hasConsent) {
          const imagePromises = getImagePromises(channelId, data);
          // eslint-disable-next-line no-await-in-loop
          const fetchedData = await Promise.all(imagePromises);

          const availablePosts = fetchedData.filter(post => post.url && post.assetUrl && !ignoredUrls.includes(post.url));
          results = results.concat(availablePosts);
        } else {
          results = results.concat(data.filter(post => post.url && !ignoredUrls.includes(post.url)));
        }

        if (!meta.currentPage || !meta.pagesTotal || meta.currentPage >= meta.pagesTotal) {
          // quit on last page
          break;
        }
      }

      if (results.length === 0 && triggerError) {
        dispatch(danger(__t(`proposal_reducer_errors.no_recent_posts.${ChannelTypeFromId[channelId]}`)));
        logError(`User ${profileId} with IG channel ${channelId} has zero posts available`);
      } else if (results.length < numberOfImages && triggerError) {
        dispatch(danger(__t(`proposal_reducer_errors.only_few_recent_posts.${ChannelTypeFromId[channelId]}`)));
      }

      return results;
    } catch (error) {
      dispatch(danger(__t(`proposal_reducer_errors.media_fetch_failed.${ChannelTypeFromId[channelId]}`)));
      return [];
    } finally {
      loadingProfileIds = getState().proposalCreatePage.loadingProfileIds;
      dispatch(setLoadingProfileIds({ ...loadingProfileIds, [profileId]: false }));
    }
  };
}

export function loadRecentMedia(profileId: number, channelId: number, page: number = 1): ThunkAction {
  return async (dispatch, getState): Promise<void> => {
    const currentState = getState().creatorRecentMedia;
    const consents = getState().consentReducer.consents;

    const channel = ChannelTypeFromId[channelId];
    const hasConsent = consents[channel];

    if (
      currentState.currentlyLoadedCreatorId === profileId
      && currentState.currentlyLoadedCreatorChannelId === channelId
      && currentState.currentlyLoadedPage >= page
    ) {
      // if already loaded this profile and page is also loaded
      return;
    }

    if (page === 1) {
      // if not returned in previous block, reset on fetching first page
      dispatch(resetMedia());
    }

    dispatch(setLoadingStatus(true));
    try {
      const {
        data,
        meta
      } = await fetchRecentImagesPage(profileId, channelId, page);

      let fetchedData:Array<ProposalImage> = [];

      if (hasConsent) {
        const imagePromises = getImagePromises(channelId, data);
        fetchedData = await Promise.all(imagePromises);

        if (channelId === ChannelType.instagram) {
          const deletedPosts = fetchedData.filter(post => !post.assetUrl);
          const deletedPostsIds = deletedPosts.map(post => getPostCodeFromUrl(post.url));

          if (deletedPostsIds.length > 0) {
            // trigger re-fetch of posts which are not available
            dispatch(deletePosts(deletedPostsIds, 'instagram'));
          }
        }

        if (channelId === ChannelType.tiktok) {
          const deletedVideos = fetchedData.filter(post => !post.assetUrl);
          const deletedVideoIds = deletedVideos.map(post => getTikTokVideoCodeFromUrl(post.url));

          if (deletedVideoIds.length > 0) {
            // trigger re-fetch of videos which are not available
            dispatch(deletePosts(deletedVideoIds, 'tiktok'));
          }
        }

        fetchedData = fetchedData.filter(post => post.assetUrl);
      } else {
        fetchedData = data;
      }

      dispatch(setMedia(fetchedData, channelId, meta, profileId, page));
    } catch (error) {
      dispatch(danger(__t(`proposal_reducer_errors.media_fetch_failed.${ChannelTypeFromId[channelId]}`)));
      throw error;
    } finally {
      dispatch(setLoadingStatus(false));
    }
  };
}

export default createReducer<State, any>('creatorRecentMedia', initialState, {
  [RESET]: () => initialState,
  [SET_LOADING_STATUS]: (state, { loadingStatus }) => ({
    ...state,
    loadingStatus
  }),
  [SET_MEDIA]: (state, { posts, meta, profileId, page, channelId }) => ({
    ...state,
    posts: uniqBy([...state.posts, ...posts], 'url'),
    isOnLastPage: meta.currentPage >= meta.pagesTotal,
    currentlyLoadedCreatorId: profileId,
    currentlyLoadedCreatorChannelId: channelId,
    currentlyLoadedPage: page
  })
});
